import { Disclosure } from "@headlessui/react";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import {
  BellAlertIcon,
  CameraIcon,
  LockClosedIcon,
  SpeakerWaveIcon,
  KeyIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { ChevronRight } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { downloadSourceFile } from "../../../../actions/CarrierAgreementMgmt/Security";
import AddButton from "../../../../components/AddButton";
import CloseIconButton from "../../../../components/CloseIconButton";
import DownloadButton from "../../../../components/DownloadButton";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { mainSelectedTab } from "../../atoms";
import { useEmployees } from "../../Employees/Confie/hooks/useEmployees";
import {
  AlarmModalState,
  CameraModalState,
  CardReaderState,
  DoorBuzzerModalState,
  FireAlarmSystemModalState,
  HvacModalState,
  officeKeyModalState,
  SafeModalState,
  SecurityAddState,
  securityViewData,
} from "../atoms/atoms";
import { useCamerasAndLocation } from "../hooks/useCamerasAndLocation";
import { useGetSecurityByRecordNumber } from "../hooks/useGetSecurityByRecordNumber";
import { useAlarmsByRecordNumber } from "../hooks/useAlarmsByRecordNumber";
import Modal from "../../../../components/Modal";
import AlarmCard from "../../../../components/AlarmCard";
import CameraCard from "../../../../components/CameraCard";
import SafeDetailsCard from "../../../../components/SafeDetailsCard";
import DoorBuzzerCard from "../../../../components/DoorBuzzerCard";
import OfficeKeyCard from "../../../../components/OfficeKeyCard";
import HvacCard from "../../../../components/HvacCard";
import { useGetCamerasByRecordNumber } from "../hooks/useGetCamerasByRecordNumber";
import { useGetSafeDetailsByRecordNumber } from "../hooks/useGetSafeDetailsByRecordNumber";
import { useGetDoorBuzzersByRecordNumber } from "../hooks/useGetDoorBuzzersByRecordNumber";
import { useGetHvacsByRecordNumber } from "../hooks/useGetHvacsByRecordNumber";
import { useGetOfficeKeysByRecordNumber } from "../hooks/useGetOfficeKeysByRecordNumber";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import CardReaderCard from "components/CardReaderCard";
import { useGetCardReaderByRecordNumber } from "../hooks/useGetCardReaderByRecordNumber";
import { useCameraTypes1 } from "../hooks/useCameraTypes1";
import { useCameraTypes2 } from "../hooks/useCameraTypes2";
import CameraFormModal from "components/CameraFormModal";
import FireAlarmSystemCard from "components/FireAlarmSystemCard";
import { useFireAlarmSystemsByRecordNumber } from "../hooks/useFireAlarmSystemsByRecordNumber";

interface SecurityDetailsProps {
  rowData: any;
  rowIsExpanded: boolean;
  handleEdit: any;
  onClickCancelShow;
  setIsAdd;
  setReset;
  viewFromOffice?: boolean;
  officeId?: any;
  onClickViewAll;
}

export const SecurityRowDetails: React.FC<SecurityDetailsProps> = ({
  rowData,
  rowIsExpanded,
  setReset,
  handleEdit,
  onClickCancelShow,
  setIsAdd,
  viewFromOffice,
  onClickViewAll,
}) => {
  const [securityDetails, setSecurityDetails] = useState<any>([]);
  const {
    data: securityData,
    isFetching,
    isSuccess,
  } = useGetSecurityByRecordNumber(rowData?.recordNumber);

  useEffect(() => {
    if (isSuccess && securityData) {
      setSecurityDetails(securityData);
    }
  }, [isSuccess, securityData]);

  const setAddState = useSetRecoilState(SecurityAddState);
  const [rowViewData, setRowViewData] = useRecoilState<any>(securityViewData);
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_security_add;
  const editPermission = PERMISSIONS.permission_security_edit;
  const [showAlarmCardModal, setShowAlarmCardModal] =
    useRecoilState(AlarmModalState);
  const [showCameraCardModal, setShowCameraCardModal] =
    useRecoilState(CameraModalState);
  const [showCardReaderModal, setShowCardReaderModal] =
    useRecoilState(CardReaderState);
  const [showFireAlarmSystemCardModal, setShowFireAlarmSystemCardModal] =
    useRecoilState(FireAlarmSystemModalState);
  const [showSafeCardModal, setShowSafeCardModal] =
    useRecoilState(SafeModalState);
  const [showDoorBuzzerCardModal, setShowDoorBuzzerCardModal] =
    useRecoilState(DoorBuzzerModalState);
  const [showOfficeKeysModal, setShowOfficeKeysModal] =
    useRecoilState(officeKeyModalState);
  const [showHvacCardModal, setShowHvacCardModal] =
    useRecoilState(HvacModalState);
  const [cameraDetails, setCameraDetails] = useState<any>([]);
  useEffect(() => {
    if (!cameraDetails || cameraDetails.length === 0) {
      setCameraDetails(rowViewData);
    }
  }, [cameraDetails, rowViewData]);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const history = useHistory();

  const { data: camAndLocation } = useCamerasAndLocation();
  const camAndLocationOption = camAndLocation?.data?.map((x) => {
    return {
      id: x.id,
      value: x.id?.toString(),
      label: x.camerasAndLocation,
    };
  });
  const { data: camTypes1 } = useCameraTypes1();
  const camTypes1Option = camTypes1?.data?.map(
    (x: { id: number; cameraType1: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.cameraType1,
      };
    }
  );

  const { data: camTypes2 } = useCameraTypes2();
  const camTypes2Option = camTypes2?.data?.map(
    (x: { id: number; cameraType2: string }) => {
      return {
        id: x.id,
        value: x.id,
        label: x.cameraType2,
      };
    }
  );

  const { data: employees } = useEmployees();
  const employeeList = employees?.map(
    (x: {
      employeenumber: number;
      employeeId: number;
      fullname: string;
      email: string;
      status: string;
      reportsToFullname: string;
      officeName: string;
      firstname: string;
      middlename: string;
      lastname: string;
    }) => {
      return {
        id: x.employeeId,
        value: x.employeeId?.toString(),
        label:
          x.firstname +
          (x.middlename ? " " + x.middlename + " " : " ") +
          x.lastname,
        email: x.email,
        status: x.status,
        manager: x.reportsToFullname,
        officeName: x.officeName,
        fullName:
          x.firstname +
          (x.middlename ? " " + x.middlename + " " : " ") +
          x.lastname,
      };
    }
  );

  const downloadFile = (fileName) => {
    try {
      downloadSourceFile(fileName).then((content) => {
        if (content.status === 200) {
          var url = window.URL.createObjectURL(content.data);
          let tempLink = document.createElement("a");
          tempLink.href = url;
          tempLink.setAttribute("download", fileName);
          tempLink.click();
          toast.fire("Success", "File downloaded successfully", "success");
        }
        if (content.status === 404) {
          toast.fire(
            "Error downloading file:",
            "The file has not been found in the system.",
            "error"
          );
        }
      });
    } catch (error) {
      toast.fire(
        "Failed to download file",
        "Server Error: Cannot download file due to: " + error,
        "error"
      );
    }
  };
  const { data: alarms } = useAlarmsByRecordNumber(rowData?.recordNumber);
  const { data: cardReader } = useGetCardReaderByRecordNumber({
    id: rowData?.recordNumber,
  });
  const { data: fireAlarmSystems, isLoading: fetchingFireAlarmSystems } = useFireAlarmSystemsByRecordNumber(rowData?.recordNumber);
  const { data: cameras } = useGetCamerasByRecordNumber(rowData?.recordNumber);
  const { data: safes } = useGetSafeDetailsByRecordNumber(
    rowData?.recordNumber
  );
  const { data: doorBuzzers } = useGetDoorBuzzersByRecordNumber(
    rowData?.recordNumber
  );
  const { data: officeKeys } = useGetOfficeKeysByRecordNumber(
    rowData?.recordNumber
  );
  const { data: hvacs } = useGetHvacsByRecordNumber(rowData?.recordNumber);

  const keysValue = rowViewData?.keysAssignedTo?.split(",");
  const filteredValue = keysValue?.map((x) =>
    employeeList?.filter((b) => b?.value === x)
  );
  const keysAssignedValue =
    filteredValue && filteredValue?.map((x) => x?.map((x) => x)[0]);
  return (
    <>
      {!isFetching && securityData && securityDetails && (
        <>
          <div className="flex mx-2 justify-between pb border-secondary border-b-2 pb-1 pb-1">
            <div className="flex gap-2 items-center">
              <div>
                <ShieldCheckIcon className="text-white bg-blue-500 rounded-md h-8 w-8 p-1" />
              </div>
              <div className="flex flex-col leading tight">
                <h3 className="text-xs font-medium text-gray-500">Security</h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  <div className="">
                    <span className="pr-2">
                      {"S-" +
                        String(securityDetails.recordNumber)?.padStart(4, "0")}
                    </span>
                  </div>
                </h1>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                {userPermissions.includes(addPermission) ? (
                  <AddButton
                    className={"opacity-100"}
                    disabled={false}
                    buttonText="Add"
                    onClick={() => {
                      setIsAdd(true);
                      setAddState(true);
                    }}
                  />
                ) : (
                  ""
                )}
                {userPermissions.includes(editPermission) ? (
                  <EditOutlineButton
                    buttonText="Edit"
                    className={""}
                    disabled={false}
                    onClick={() => {
                      handleEdit(securityDetails);
                    }}
                  />
                ) : (
                  ""
                )}
              </div>

              <CloseIconButton onClick={() => onClickCancelShow()} />
            </div>
          </div>
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <div className="mt-4 m-2 bg-white rounded-lg box-shadow">
                <div className="text-quinary font-bold font-sans py-2 px-6">
                  Main Section
                </div>
                <div className="">
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Record Number:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          <span className="pr-2">
                            {"S-" +
                              String(securityDetails.recordNumber)?.padStart(
                                4,
                                "0"
                              )}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Office Name:
                        </label>
                        <span className="pr-2">
                          <div
                            className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                            onClick={() => {
                              const url = "/officesettings/officesetting";
                              history.push({
                                pathname: url,
                                state: {
                                  officeId: securityDetails.officeId,
                                  action: "Security",
                                },
                              });
                              let userTabs = [
                                {
                                  to: "",
                                },
                              ];
                              [...OfficeTabs].forEach((tabs) => {
                                let temp = [...userPermissions].filter(
                                  (user) => user === tabs.permission
                                );
                                if (temp.length > 0)
                                  userTabs.push({
                                    to: tabs.to,
                                  });
                              });
                              userTabs.shift();
                              const index = userTabs.findIndex(
                                (c) => c.to === url
                              );
                              setSelectedIndex(index);
                            }}
                          >
                            {securityDetails.officeName}
                          </div>
                        </span>
                      </div>
                    </div>
                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Office State:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {securityDetails.officeState}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Office Brand:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {securityDetails.officeBrand}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Office Status:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {securityDetails.officeStatus}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {/* 2nd row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />

                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Alarm Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {alarms?.map((alarm) => {
                              return (
                                <div key={alarm.id}>
                                  <AlarmCard
                                    alarm={alarm}
                                    isEditState={false}
                                    onClickView={() => {
                                      setShowAlarmCardModal(true);
                                      setRowViewData(alarm);
                                    }}
                                    onClickEdit={() => { }}
                                    onClickDelete={() => { }}
                                    hasDeletePermission={undefined}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Disclosure.Panel>
                      {viewFromOffice && (
                        <div className="pl-8 ml-8">
                          <button
                            onClick={() => onClickCancelShow()}
                            className="hover:scale-90 bg-quinary flex h-3 sm:h-4 sm:w-20 sm:py-2.5 text-white xl:h-6 xl:w-24 lg:px-1 lg:py-2 hover:bg-h quinary hover:border-h quinary flex-row shadow-md items-center justify-around text-xs3 border rounded-full px-4 "
                          >
                            {" "}
                            View All
                          </button>{" "}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 3rd row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Camera Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {Array.isArray(cameras) && cameras.length > 0 ? (
                              cameras.map((camera) => (
                                <CameraCard
                                  key={camera.id}
                                  camera={camera}
                                  isEditState={false}
                                  onClickView={() => {
                                    setShowCameraCardModal(true);
                                    setCameraDetails(camera);
                                  }}
                                  onClickEdit={() => { }}
                                  onClickDelete={() => { }}
                                  hasDeletePermission={undefined}
                                  camAndLocation={camAndLocation}
                                />
                              ))
                            ) : (
                              <div></div>
                            )}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* CARD READER SECTION */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Card Reader Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {cardReader?.map((reader) => {
                              return (
                                <div key={reader.id}>
                                  <CardReaderCard
                                    editHidden
                                    cardReader={reader}
                                    isEditState={false}
                                    onClickView={() => {
                                      setShowCardReaderModal(true);
                                      setRowViewData(reader);
                                    }}
                                    onClickEdit={() => { }}
                                    onClickDelete={() => { }}
                                    hasDeletePermission={undefined}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>

          {/* FIRE ALARM SYSTEM SECTION */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />

                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Fire Alarm System Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {(fetchingFireAlarmSystems ? [] : fireAlarmSystems)?.map((fireAlarmSystem) => {
                              return (
                                <div key={fireAlarmSystem.id}>
                                  <FireAlarmSystemCard
                                    key={fireAlarmSystem.id}
                                    fireAlarmSystem={fireAlarmSystem}
                                    isEditState={false}
                                    onClickView={() => {
                                      setShowFireAlarmSystemCardModal(true);
                                      setRowViewData(fireAlarmSystem);
                                    }}
                                    onClickEdit={() => { }}
                                    onClickDelete={() => { }}
                                    hasDeletePermission={undefined}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Disclosure.Panel>
                      {viewFromOffice && (
                        <div className="pl-8 ml-8">
                          <button
                            onClick={() => onClickCancelShow()}
                            className="hover:scale-90 bg-quinary flex h-3 sm:h-4 sm:w-20 sm:py-2.5 text-white xl:h-6 xl:w-24 lg:px-1 lg:py-2 hover:bg-h quinary hover:border-h quinary flex-row shadow-md items-center justify-around text-xs3 border rounded-full px-4 "
                          >
                            {" "}
                            View All
                          </button>{" "}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 4th row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Safe Details Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {safes?.map((safe) => (
                              <div key={safe.id}>
                                <SafeDetailsCard
                                  safe={safe}
                                  isEditState={false}
                                  onClickView={() => {
                                    setRowViewData(safe);
                                    setShowSafeCardModal(true);
                                  }}
                                  onClickEdit={undefined}
                                  onClickDelete={undefined}
                                  hasDeletePermission={undefined}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 5th Row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Door Buzzer Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="">
                          <div>
                            <div className="w-full borderContainer pb-4 bg-white px-8">
                              <div className="grid gap-3 grid-cols-5">
                                {doorBuzzers?.map((doorBuzzer) => (
                                  <div key={doorBuzzer.id}>
                                    <DoorBuzzerCard
                                      doorBuzzer={doorBuzzer}
                                      isEditState={undefined}
                                      onClickView={() => {
                                        setShowDoorBuzzerCardModal(true);
                                        setRowViewData(doorBuzzer);
                                      }}
                                      onClickEdit={undefined}
                                      onClickDelete={undefined}
                                      hasDeletePermission={undefined}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 6th Row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Office Keys Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {officeKeys?.map((officeKey) => (
                              <div key={officeKey.id}>
                                <OfficeKeyCard
                                  officeKey={officeKey}
                                  isEditState={undefined}
                                  onClickView={() => {
                                    setShowOfficeKeysModal(true);
                                    setRowViewData(officeKey);
                                  }}
                                  onClickEdit={undefined}
                                  onClickDelete={undefined}
                                  hasDeletePermission={undefined}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 7th row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          HVAC Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="w-full borderContainer pb-4 bg-white px-8">
                          <div className="grid gap-3 grid-cols-5">
                            {hvacs?.map((hvac) => (
                              <div key={hvac.id}>
                                <HvacCard
                                  hvac={hvac}
                                  isEditState={undefined}
                                  onClickView={() => {
                                    setShowHvacCardModal(true);
                                    setRowViewData(hvac);
                                  }}
                                  onClickEdit={undefined}
                                  onClickDelete={undefined}
                                  hasDeletePermission={undefined}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* 8th row */}
          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <>
                    <div className="m-2 bg-white rounded-lg box-shadow">
                      <Disclosure.Button className="disclosureBtn ml-2">
                        <ChevronRight
                          className={`${open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                        />
                        <div className="text-quinary font-bold font-sans py-2 px-2">
                          Files and Images Section
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="text-xxs">
                        <div className="">
                          <div className="grid grid-cols-1">
                            <div className="px-8 pb-4 divide-y">
                              <div className="grid grid-cols-2">
                                <label className="col-span-1 expandedLabel fontExpand">
                                  File Category:
                                </label>
                                <label className="col-span-1 expandedLabel fontExpand">
                                  Attachment/s:
                                </label>
                              </div>
                              {securityDetails?.fileCategoryList?.map((x) => {
                                return (
                                  <div className="grid grid-cols-2">
                                    <div className="grid grid-cols-3">
                                      <div className="col-span-2 flex items-center expandedData fontExpand">
                                        <span className="">
                                          {x.securityFileCategory1}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="py-2 divide-y">
                                      <div className="grid grid-cols-5 gap-4 expandedData fontExpand items-center rounded-md p-2 bg-LightWhite">
                                        {securityDetails?.fileNames
                                          ?.filter(
                                            (y) => y.fileCategoryId === x.id
                                          )
                                          .map((file) => {
                                            return (
                                              <>
                                                {" "}
                                                <div className="col-span-4">
                                                  {file?.securityFileName}
                                                </div>{" "}
                                                <div>
                                                  <DownloadButton
                                                    buttonValue="Download"
                                                    className=""
                                                    onClick={() =>
                                                      downloadFile(
                                                        file?.securityFileName
                                                      )
                                                    }
                                                    disabled={undefined}
                                                  />
                                                </div>
                                              </>
                                            );
                                          })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  </>
                )}
              </Disclosure>
            ) : (
              ""
            )}
          </div>
          {/* Alarm card modal */}
          {showAlarmCardModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showAlarmCardModal}
              onClose={() => {
                setShowAlarmCardModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <BellAlertIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Alarm Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.vendorName}
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 px-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Name:
                    </label>
                    <div className="col-span-2 expandedData fontExpand ">
                      <span className="pr-2">
                        <div
                          className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                          onClick={() => {
                            const url = "/officesettings/security";
                            history.push({
                              pathname: "/vendortab/vendor",
                              state: {
                                vendorId: rowViewData?.vendorId,
                                securityData: rowViewData,
                                action: "SecurityAlarm",
                              },
                            });
                            window.location.reload();
                            let userTabs = [
                              {
                                to: "",
                              },
                            ];
                            [...OfficeTabs].forEach((tabs) => {
                              let temp = [...userPermissions].filter(
                                (user) => user === tabs.permission
                              );
                              if (temp.length > 0)
                                userTabs.push({
                                  to: tabs.to,
                                });
                            });
                            userTabs.shift();
                            const index = userTabs.findIndex(
                              (c) => c.to === url
                            );
                            setSelectedIndex(index);
                          }}
                        >
                          {rowViewData?.vendorName}
                        </div>
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Phone:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.vendorPhone !== "null"
                        ? rowViewData?.vendorPhone
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      {" "}
                      Account Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.accountNumber}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Contract Renewal Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.contractRenewalDate
                        ? new Date(
                          rowViewData?.contractRenewalDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Alarm Site Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.alarmSiteNumber}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Data Connection:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.dataConnectionName}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Install Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.alarmInstallDate
                        ? new Date(
                          rowViewData?.alarmInstallDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      IP Address:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.ipaddress}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      IP Gateway:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.ipGateway}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Subnet Mask:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.subnetMask}
                    </div>
                  </div>
                </div>

                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      DNS:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.dns}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Connection Details:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.connectionDetails}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Alarm Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.alarmTypeName}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number Of Motion Sensors:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfMotionSensors}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number Of Door Contact:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfDoorContacts}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number Of Panic Buttons:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfPanicButtons}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Any Additional Special Equipment:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.specialEquipment}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Cancellation Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.cancellationDate
                        ? new Date(
                          rowViewData?.cancellationDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Port Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.portNumber}
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {/* Camera card modal */}
          {showCameraCardModal && (
            <CameraFormModal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showCameraCardModal}
              onClose={() => setShowCameraCardModal(false)}
              hasCloseButton={false}
            >
              {cameraDetails && (
                <>
                  {/* Header */}
                  <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                    <CameraIcon className="text-blue-500 rounded-md h-6 w-6" />
                    <div className="flex flex-col leading-tight">
                      <h3 className="text-xs1 font-medium text-gray-500">
                        Camera Section
                      </h3>
                      <h1 className="font-bold blueAndBold uppercase">
                        {cameraDetails.cameraModel} {cameraDetails.cameraMake}
                      </h1>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    {/* Left Column */}
                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Camera Make:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.cameraMake}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Camera Model:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.cameraModel}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Install Date:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.cameraInstallDate
                            ? new Date(
                              cameraDetails.cameraInstallDate
                            ).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          DVR Type:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.dvrType}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          DVR Recording Max:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.dvrRecordingMax}
                        </div>
                      </div>
                    </div>
                    {/* Right Column */}
                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Camera System:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.cameraSystem}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          IP Address:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.cameraIpAddress}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          IP Gateway:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.ipGateway}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Subnet Mask:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.subnetMask}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          DNS:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {cameraDetails.dns}
                        </div>
                      </div>
                    </div>

                    {/* Camera Locations */}
                    <div className="col-span-2">
                      <Disclosure defaultOpen>
                        {({ open }) => (
                          <div className="m-2 ">
                            <Disclosure.Panel className="text-xxs grid grid-cols-2 gap-4">
                              {cameraDetails?.cameraLocationsDto?.map((loc) => (
                                <div
                                  key={loc.id}
                                  className="p-2  rounded-lg box-shadow"
                                >
                                  <div className="grid grid-cols-3 p-2">
                                    <label className="col-span-1 mx-1 expandedLabel fontExpand">
                                      Number of Cameras and Location:
                                    </label>
                                    <div className="col-span-2 expandedData fontExpand">
                                      {loc.camerasAndLocationId
                                        ?.split(",")
                                        .map((id) => {
                                          const matched =
                                            camAndLocationOption?.find(
                                              (option) =>
                                                option.id?.toString() ===
                                                id.trim()
                                            );
                                          return matched?.label ?? id.trim();
                                        })
                                        .join(", ")}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 p-2">
                                    <label className="col-span-1 mx-1 expandedLabel fontExpand">
                                      Camera Type 1:
                                    </label>
                                    <div className="col-span-2 expandedData fontExpand">
                                      {camTypes1Option?.find(
                                        (option) =>
                                          option.id === loc.cameraType1
                                      )?.label ?? loc.cameraType1}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 p-2">
                                    <label className="col-span-1 mx-1 expandedLabel fontExpand">
                                      Camera Type 2:
                                    </label>
                                    <div className="col-span-2 expandedData fontExpand">
                                      {camTypes2Option?.find(
                                        (option) =>
                                          option.id === loc.cameraType2
                                      )?.label ?? loc.cameraType2}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </Disclosure.Panel>
                          </div>
                        )}
                      </Disclosure>
                    </div>
                  </div>
                </>
              )}
            </CameraFormModal>
          )}
          {/* Card Reader modal */}
          {showCardReaderModal && (
            <Modal
              modalFilterStat=""
              className="w-full max-w-7xl h-[80vh] overflow-hidden"
              width={""}
              title={""}
              isOpen={showCardReaderModal}
              onClose={() => {
                setShowCardReaderModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Card Reader Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.vendorName}
                  </h1>
                </div>
              </div>
              <div className=" h-[65vh] overflow-y-auto  mt-8">
                {/* General sec */}
                <section className="bg-white box-shadow rounded-lg mb-4">
                  <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                    General Details
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="p-2 divide-y ">
                      <div className="grid grid-cols-2 px-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor Name:
                        </label>
                        <div className="col-span-1 expandedData fontExpand ">
                          <span className="pr-2">
                            <div
                              className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                              onClick={() => {
                                const url = "/officesettings/security";
                                history.push({
                                  pathname: "/vendortab/vendor",
                                  state: {
                                    vendorId: rowViewData?.vendorId,
                                    securityData: rowViewData,
                                    action: "SecurityCardReader",
                                  },
                                });
                                window.location.reload();
                                let userTabs = [
                                  {
                                    to: "",
                                  },
                                ];
                                [...OfficeTabs].forEach((tabs) => {
                                  let temp = [...userPermissions].filter(
                                    (user) => user === tabs.permission
                                  );
                                  if (temp.length > 0)
                                    userTabs.push({
                                      to: tabs.to,
                                    });
                                });
                                userTabs.shift();
                                const index = userTabs.findIndex(
                                  (c) => c.to === url
                                );
                                setSelectedIndex(index);
                              }}
                            >
                              {rowViewData?.vendorName}
                            </div>
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Account Number:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.accountNumberBrivo}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Billing Account Number:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.accountNumberBilling}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor Phone:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.vendorPhone !== "null"
                            ? rowViewData?.vendorPhone
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor CS#/Site#:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.vendorCS}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Brivo Site Number
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.brivoSiteNumber}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Confie Site Number
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.confieSiteNumber}
                        </div>
                      </div>
                    </div>

                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Data Connection Equipment
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.dataConnectionEquipment}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Data Connection Port
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.dataConnectionPort}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          IP Address Setting Option
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.ipAddressSettingOption === true
                            ? "DHCP"
                            : rowViewData?.ipAddressSettingOption === false
                              ? "Static IP Address"
                              : ""}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          IP Address Setting
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.ipAddress}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Install Date:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.installDate
                            ? new Date(
                              rowViewData?.installDate
                            )?.toLocaleDateString()
                            : ""}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Cancellation Date:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cancellationDate
                            ? new Date(
                              rowViewData?.cancellationDate
                            )?.toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Active
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.isActive ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* warranty sec */}
                <section className="bg-white box-shadow rounded-lg mb-4">
                  <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                    Warranty Details
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="p-2 divide-y ">
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Warranty
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.isWarranty === true
                            ? "Yes"
                            : rowViewData?.isWarranty === false
                              ? "No"
                              : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Warranty Coverage:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.warrantyCoverage}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Additional Description:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.additionalDescription}
                        </div>
                      </div>
                    </div>

                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Coverage Start Date:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.coverageStartDate
                            ? new Date(
                              rowViewData?.coverageStartDate
                            )?.toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Coverage End Date:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.coverageEndDate
                            ? new Date(
                              rowViewData?.coverageEndDate
                            )?.toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* other card sec */}
                <section className="bg-white box-shadow rounded-lg mb-4">
                  <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                    Other Card Details
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="p-2 divide-y ">
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Card Type:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cardType}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Parking Garage Key Card Access
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.isKeyCardAccess === true
                            ? "Yes"
                            : rowViewData?.isKeyCardAccess === false
                              ? "No"
                              : ""}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Parking Card Format:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.parkingCardFormat}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Parking Site Number:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.parkingSiteNumber}
                        </div>
                      </div>
                    </div>

                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Card Name:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cardName}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Company Name:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cardCompanyName}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Address:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cardAddress}
                        </div>
                      </div>{" "}
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Hours:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {/* {rowViewData?.cardHours} */}
                          <div className="col-span-1 expandedData fontExpand">
                            {rowViewData?.cardHours
                              ? new Date(
                                `1970-01-01T${rowViewData?.cardHours}`
                              ).toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                              : ""}
                          </div>
                        </div>
                      </div>{" "}
                      <div className="grid grid-cols-2 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Email Address:
                        </label>
                        <div className="col-span-1 expandedData fontExpand">
                          {rowViewData?.cardEmailAddress}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Site Admin sec */}

                {rowViewData?.siteAdmins?.some(
                  (admin) => !admin.isMasterAdmin
                ) && (
                    <section className="pb-6  mt-6 px-2  bg-white box-shadow rounded-lg ">
                      <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                        Site Admin Details
                      </div>

                      <div className="overflow-x-auto px-4">
                        <table className="min-w-full table-auto border border-gray-300 ">
                          <thead>
                            <tr className="bg-gray-100 text-blue-800 text-left text-sm font-semibold">
                              <th className="py-2 px-4 w-[15%]">Site Admin</th>
                              <th className="py-2 px-4 w-[28.33%]">Name</th>
                              <th className="py-2 px-4 w-[28.33%]">Email</th>
                              <th className="py-2 px-4 w-[28.33%]">
                                Phone Number
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowViewData?.siteAdmins
                              ?.filter((admin) => !admin.isMasterAdmin)
                              .map((admin, index) => (
                                <tr
                                  key={admin.id}
                                  className="border-t border-gray-300 text-xs font-semibold text-gray-700"
                                >
                                  <td className="py-2 px-4">
                                    Site Admin {index + 1}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.name || "-"}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.email || "-"}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.phoneNumber || "-"}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                {rowViewData?.siteAdmins?.some(
                  (admin) => admin.isMasterAdmin
                ) && (
                    <section className="pb-6  mt-6 px-2  bg-white box-shadow rounded-lg ">
                      <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                        Master Admin Details
                      </div>

                      <div className="overflow-x-auto mx-4">
                        <table className="min-w-full table-auto border border-gray-300">
                          <thead>
                            <tr className="bg-gray-100 text-blue-800 text-left text-sm font-semibold">
                              <th className="py-2 px-4 w-[15%]">Master Admin</th>
                              <th className="py-2 px-4 w-[28.33%]">Name</th>
                              <th className="py-2 px-4 w-[28.33%]">Email</th>
                              <th className="py-2 px-4 w-[28.33%]">
                                Phone Number
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowViewData?.siteAdmins
                              ?.filter((admin) => admin.isMasterAdmin)
                              .map((admin, index) => (
                                <tr
                                  key={admin.id}
                                  className="border-t border-gray-300 text-xs font-semibold text-gray-700"
                                >
                                  <td className="py-2 px-4">
                                    Master Admin {index + 1}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.name || "-"}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.email || "-"}
                                  </td>
                                  <td className="py-2 px-4">
                                    {admin.phoneNumber || "-"}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                {/* Group Info Sec */}
                {rowViewData?.groupInfos?.length > 0 && (
                  <section className="pb-4 mt-4 ">
                    {rowViewData.groupInfos.map((group, index) => (
                      <div
                        key={group.id}
                        className="mb-6 bg-white box-shadow rounded-lg  border border-t pb-6"
                      >
                        {/* Group Header */}
                        <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                          Group Information {index + 1}
                        </div>

                        {/* Group Content */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                          {/* Left Column */}
                          <div className="p-2 divide-y">
                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Group Title:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.groupTitle || ""}
                              </div>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="p-2 divide-y">
                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Staff Group:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.staffGroup || ""}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="text-blue-800 font-semibold text-sm py-4 pl-6">
                          Hours
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                          {/* Left Column */}
                          <div className="p-2 divide-y">
                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Monday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.monday_Hours === "-"
                                  ? ""
                                  : group.monday_Hours}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Tuesday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.tuesday_Hours === "-"
                                  ? ""
                                  : group.tuesday_Hours}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Wednesday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.wednesday_Hours === "-"
                                  ? ""
                                  : group.wednesday_Hours}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Thursday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.thursday_Hours === "-"
                                  ? ""
                                  : group.thursday_Hours}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Friday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.friday_Hours === "-"
                                  ? ""
                                  : group.friday_Hours}
                              </div>
                            </div>
                          </div>

                          <div className="p-2 divide-y ">
                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Saturday{" "}
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.saturday_Hours === "-"
                                  ? ""
                                  : group.saturday_Hours}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 p-2">
                              <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                Sunday:
                              </label>
                              <div className="col-span-1 expandedData fontExpand">
                                {group.sunday_Hours === "-"
                                  ? ""
                                  : group.sunday_Hours}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Door Access */}

                        <div className="px-6 pt-4">
                          <label className="block expandedLabel fontExpand font-semibold text-blue-800 mb-1">
                            Door Access
                          </label>

                          {group.doors?.some((door) => door) ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full table-auto border border-gray-300 border-collapse">
                                <thead>
                                  <tr className="bg-gray-100 text-blue-800 text-left text-sm font-semibold">
                                    <th className="py-2 px-4 w-[50%]">
                                      Door #
                                    </th>
                                    <th className="py-2 px-4 w-[50%]">
                                      Door Access Name
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="text-xs font-semibold text-black">
                                  {group.doors.map((door, i) => {
                                    if (!door) return null;
                                    return (
                                      <tr
                                        key={i}
                                        className="border-t border-gray-300"
                                      >
                                        <td className="px-4 py-2">{`Door ${i + 1
                                          }`}</td>
                                        <td className="px-4 py-2">{door}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="expandedData font-normal"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </section>
                )}
              </div>
            </Modal>
          )}
          {/* Fire Alarm System card modal */}
          {showFireAlarmSystemCardModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showFireAlarmSystemCardModal}
              onClose={() => {
                setShowFireAlarmSystemCardModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <BellAlertIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Fire Alarm System Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.vendorName}
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 px-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Name:
                    </label>
                    <div className="col-span-2 expandedData fontExpand ">
                      <span className="pr-2">
                        <div
                          className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                          onClick={() => {
                            const url = "/officesettings/security";
                            history.push({
                              pathname: "/vendortab/vendor",
                              state: {
                                vendorId: rowViewData?.vendorId,
                                securityData: rowViewData,
                                action: "SecurityFireAlarmSystem",
                              },
                            });
                            window.location.reload();
                            let userTabs = [
                              {
                                to: "",
                              },
                            ];
                            [...OfficeTabs].forEach((tabs) => {
                              let temp = [...userPermissions].filter(
                                (user) => user === tabs.permission
                              );
                              if (temp.length > 0)
                                userTabs.push({
                                  to: tabs.to,
                                });
                            });
                            userTabs.shift();
                            const index = userTabs.findIndex(
                              (c) => c.to === url
                            );
                            setSelectedIndex(index);
                          }}
                        >
                          {rowViewData?.vendorName}
                        </div>
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Phone:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.vendorPhone !== "null"
                        ? rowViewData?.vendorPhone
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      {" "}
                      Account Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.vendorAccountNumber}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Site Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.siteNumber}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Data Connection:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.dataConnectionName}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Install Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.installDate
                        ? new Date(
                          rowViewData?.installDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      IP Address:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.ipaddress}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      IP Gateway:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.ipGateway}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Subnet Mask:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.subnetMask}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      DNS:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.dns}
                    </div>
                  </div>
                </div>

                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Phone Connection:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.phoneConnection}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Connection Details:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.connectionDetails}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Fire Alarm Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.alarmTypeName}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number Of Sensors:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfFireAlarmSensorsId}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number Of Floors:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfFloorsId}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Any Additional Special Equipment:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.specialEquipment}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Cancellation Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.cancellationDate
                        ? new Date(
                          rowViewData?.cancellationDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Port Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.portNumber}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Active Account:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.isActive ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {/* Safe card modal */}
          {showSafeCardModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showSafeCardModal}
              onClose={() => {
                setShowSafeCardModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <LockClosedIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Safe Details Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.safeModel} {rowViewData?.safeMake} {""}
                    {rowViewData?.safeType}
                  </h1>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Safe Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">{rowViewData?.safeType}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Is Safe Bolted to the Floor:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.isSafeBolted === true
                        ? "Yes"
                        : rowViewData?.isSafeBolted === null
                          ? ""
                          : "No"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Safe Code:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.safeCode}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Safe Model:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.safeModel}
                    </div>
                  </div>
                </div>
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      {" "}
                      Safe Make:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.safeMake}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Safe Serial Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.safeSerialNumber}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Safe Location:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.safeLocation}
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {/* Door Buzzer card modal */}
          {showDoorBuzzerCardModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showDoorBuzzerCardModal}
              onClose={() => {
                setShowDoorBuzzerCardModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <SpeakerWaveIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Door Buzzer Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.siteNumber} {rowViewData?.connection}{" "}
                    {rowViewData?.type} {""}
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Site Number:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">{rowViewData?.siteNumber} </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Connection:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.connection}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Install Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.dbinstallDate
                        ? new Date(
                          rowViewData?.dbinstallDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.type}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      # of Button Select List:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.numberOfButtonSelectedId}
                    </div>
                  </div>
                </div>
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Warranty:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.warranty === true
                        ? "Yes"
                        : rowViewData?.warranty === false
                          ? "No"
                          : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Warranty Length:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.warrantyLength}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Warranty Coverage:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.warrantyCoverage}
                    </div>
                  </div>
                  {/* <div className="grid grid-cols-3 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Vendor Type:
                                </label>
                                <div className="col-span-2 expandedData fontExpand">
                                  {securityDetails.typeOfVendor}
                                </div>
                              </div> */}
                  {rowViewData?.typeOfVendor && (
                    <>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor Name:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {rowViewData?.dbinstallVendorName}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor Address:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {rowViewData?.dbinstallVendorAddress}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Vendor Phone:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {rowViewData?.dbinstallVendorPhone !== "null"
                            ? rowViewData?.dbinstallVendorPhone
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Contact:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {rowViewData?.dbinstallVendorContact}
                        </div>
                      </div>{" "}
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3">
                <div className="p-2 divide-y">
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <div className="m-2 bg-white rounded-lg box-shadow">
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRight
                              className={`${open ? "transform rotate-90" : " text-gray-500"
                                } text-gray-500 w-5 h-5 `}
                            />
                            <div className="text-quinary font-bold font-sans text-sm py-2 px-2">
                              Install Vendor Section
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xxs">
                            <div className="">
                              <div className="grid grid-cols-1 sm:grid-cols-1">
                                <div className="p-2 divide-y">
                                  <div className="grid grid-cols-1 p-2">
                                    <div className="flex  flex-col">
                                      <div>
                                        <span className="text-xs">
                                          <div
                                            className="blueAndBold uppercase underline expandedData fontExpand text-xs cursor-pointer"
                                            onClick={() => {
                                              const url =
                                                "/officesettings/security";
                                              history.push({
                                                pathname: "/vendortab/vendor",
                                                state: {
                                                  vendorId:
                                                    rowViewData?.dbinstallVendorId,
                                                  securityData: rowViewData,
                                                  action: "SecurityDoorBuzzer",
                                                },
                                              });
                                              window.location.reload();
                                              let userTabs = [
                                                {
                                                  to: "",
                                                },
                                              ];
                                              [...OfficeTabs].forEach(
                                                (tabs) => {
                                                  let temp = [
                                                    ...userPermissions,
                                                  ].filter(
                                                    (user) =>
                                                      user === tabs.permission
                                                  );
                                                  if (temp.length > 0)
                                                    userTabs.push({
                                                      to: tabs.to,
                                                    });
                                                }
                                              );
                                              userTabs.shift();
                                              const index = userTabs.findIndex(
                                                (c) => c.to === url
                                              );
                                              setSelectedIndex(index);
                                            }}
                                          >
                                            {rowViewData?.dbinstallVendorName
                                              ? rowViewData?.dbinstallVendorName
                                              : "No Vendor"}
                                          </div>
                                        </span>
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbinstallVendorAddress
                                          ? rowViewData?.dbinstallVendorAddress
                                          : "No Address"}
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbinstallVendorPhone
                                          ? rowViewData?.dbinstallVendorPhone
                                          : "No Phone"}
                                      </div>
                                      <div className="mainLabeltextColor">
                                        {rowViewData?.dbinstallVendorContact
                                          ? rowViewData?.dbinstallVendorContact
                                          : "No Contact"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Disclosure.Panel>
                        </div>
                      </>
                    )}
                  </Disclosure>
                </div>
                <div className="p-2 divide-y">
                  {" "}
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <div className="m-2 bg-white rounded-lg box-shadow">
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRight
                              className={`${open ? "transform rotate-90" : " text-gray-500"
                                } text-gray-500 w-5 h-5 `}
                            />
                            <div className="text-quinary font-bold font-sans text-sm py-2 px-2">
                              Warranty Vendor Section
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xxs">
                            <div className="">
                              <div className="grid grid-cols-1 sm:grid-cols-1">
                                <div className="p-2 divide-y">
                                  <div className="grid grid-cols-1 p-2">
                                    <div className="flex  flex-col">
                                      <div>
                                        <span className="text-xs">
                                          <div
                                            className="blueAndBold uppercase underline expandedData fontExpand text-xs cursor-pointer"
                                            onClick={() => {
                                              const url =
                                                "/officesettings/security";
                                              history.push({
                                                pathname: "/vendortab/vendor",
                                                state: {
                                                  vendorId:
                                                    rowViewData?.dbwarrantyVendorId,
                                                  securityData: rowViewData,
                                                  action: "SecurityDoorBuzzer",
                                                },
                                              });
                                              window.location.reload();
                                              let userTabs = [
                                                {
                                                  to: "",
                                                },
                                              ];
                                              [...OfficeTabs].forEach(
                                                (tabs) => {
                                                  let temp = [
                                                    ...userPermissions,
                                                  ].filter(
                                                    (user) =>
                                                      user === tabs.permission
                                                  );
                                                  if (temp.length > 0)
                                                    userTabs.push({
                                                      to: tabs.to,
                                                    });
                                                }
                                              );
                                              userTabs.shift();
                                              const index = userTabs.findIndex(
                                                (c) => c.to === url
                                              );
                                              setSelectedIndex(index);
                                            }}
                                          >
                                            {rowViewData?.dbwarrantyVendorName
                                              ? rowViewData?.dbwarrantyVendorName
                                              : "No Vendor"}
                                          </div>
                                        </span>
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbwarrantyVendorAddress
                                          ? rowViewData?.dbwarrantyVendorAddress
                                          : "No Address"}
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbwarrantyVendorPhone
                                          ? rowViewData?.dbwarrantyVendorPhone
                                          : "No Phone"}
                                      </div>
                                      <div className="mainLabeltextColor">
                                        {rowViewData?.dbwarrantyVendorContact
                                          ? rowViewData?.dbwarrantyVendorContact
                                          : "No Contact"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Disclosure.Panel>
                        </div>
                      </>
                    )}
                  </Disclosure>
                </div>
                <div className="p-2 divide-y">
                  {" "}
                  <Disclosure defaultOpen>
                    {({ open }) => (
                      <>
                        <div className="m-2 bg-white rounded-lg box-shadow">
                          <Disclosure.Button className="disclosureBtn ml-2">
                            <ChevronRight
                              className={`${open ? "transform rotate-90" : " text-gray-500"
                                } text-gray-500 w-5 h-5 `}
                            />
                            <div className="text-quinary font-bold font-sans text-sm py-2 px-2">
                              Servicing Vendor Section
                            </div>
                          </Disclosure.Button>
                          <Disclosure.Panel className="text-xxs">
                            <div className="">
                              <div className="grid grid-cols-1 sm:grid-cols-1">
                                <div className="p-2 divide-y">
                                  <div className="grid grid-cols-1 p-2">
                                    <div className="flex  flex-col">
                                      <div>
                                        <span className="text-xs">
                                          <div
                                            className="blueAndBold uppercase underline expandedData fontExpand text-xs cursor-pointer"
                                            onClick={() => {
                                              const url =
                                                "/officesettings/security";
                                              history.push({
                                                pathname: "/vendortab/vendor",
                                                state: {
                                                  vendorId:
                                                    rowViewData?.dbservicingVendorId,
                                                  securityData: rowViewData,
                                                  action: "SecurityDoorBuzzer",
                                                },
                                              });
                                              window.location.reload();
                                              let userTabs = [
                                                {
                                                  to: "",
                                                },
                                              ];
                                              [...OfficeTabs].forEach(
                                                (tabs) => {
                                                  let temp = [
                                                    ...userPermissions,
                                                  ].filter(
                                                    (user) =>
                                                      user === tabs.permission
                                                  );
                                                  if (temp.length > 0)
                                                    userTabs.push({
                                                      to: tabs.to,
                                                    });
                                                }
                                              );
                                              userTabs.shift();
                                              const index = userTabs.findIndex(
                                                (c) => c.to === url
                                              );
                                              setSelectedIndex(index);
                                            }}
                                          >
                                            {rowViewData?.dbservicingVendorName
                                              ? rowViewData?.dbservicingVendorName
                                              : "No Vendor"}
                                          </div>
                                        </span>
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbservicingVendorAddress
                                          ? rowViewData?.dbservicingVendorAddress
                                          : "No Address"}
                                      </div>
                                      <div className="text-xs">
                                        {rowViewData?.dbservicingVendorPhone
                                          ? rowViewData?.dbservicingVendorPhone
                                          : "No Phone"}
                                      </div>
                                      <div className="mainLabeltextColor">
                                        {rowViewData?.dbservicingVendorContact
                                          ? rowViewData?.dbservicingVendorContact
                                          : "No Contact"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Disclosure.Panel>
                        </div>
                      </>
                    )}
                  </Disclosure>
                </div>
              </div>
            </Modal>
          )}
          {/* HVAC card modal */}
          {showHvacCardModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showHvacCardModal}
              onClose={() => {
                setShowHvacCardModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <SunIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    HVAC Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.numberOfUnitsId} {rowViewData?.unitType} {""}
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Number of Units:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {rowViewData?.numberOfUnitsId}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Unit Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.unitType}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Ton Capacity:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.tonCapacity}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Make:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.make}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Model:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.model}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Serial #:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.serialNumber}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Warranty Compressor:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.warrantyCompressor}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Warranty Labor:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.warrantyLabor}
                    </div>
                  </div>
                </div>
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Install Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.hvacinstallDate
                        ? new Date(
                          rowViewData?.hvacinstallDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Preventive Maintenance Plan:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.preventiveMaintenancePlan}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Maintenance Company:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.maintenanceCompany}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Requires Maintenance:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.requiresMaintenanceName}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      First Maintenance Date:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.firstMaintenanceDate
                        ? new Date(
                          rowViewData?.firstMaintenanceDate
                        )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Maintenance Periodicity:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      {rowViewData?.maintenancePeriodicity}
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {/* Office keys card modal */}
          {showOfficeKeysModal && (
            <Modal
              modalFilterStat=""
              className=""
              width={""}
              title={""}
              isOpen={showOfficeKeysModal}
              onClose={() => {
                setShowOfficeKeysModal(false);
              }}
              hasCloseButton={false}
            >
              <div className="flex gap-2 items-center border-secondary border-b-2 pb-2">
                <div>
                  <KeyIcon className="text-blue-500 rounded-md h-6 w-6" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs1 font-medium text-gray-500">
                    Office Keys Section
                  </h3>
                  <h1 className="font-bold blueAndBold uppercase">
                    {rowViewData?.lockSmithVendor} - {rowViewData?.numberOfKeys}{" "}
                    {""}
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Locksmith Vendor:
                    </label>
                    <span className="pr-2">
                      <div
                        className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                        onClick={() => {
                          const url = "/officesettings/security";
                          history.push({
                            pathname: "/vendortab/vendor",
                            state: {
                              vendorId: rowViewData?.lockSmithVendorId,
                              securityData: rowViewData,
                              action: "SecurityOfficeKey",
                            },
                          });
                          window.location.reload();
                          let userTabs = [
                            {
                              to: "",
                            },
                          ];
                          [...OfficeTabs].forEach((tabs) => {
                            let temp = [...userPermissions].filter(
                              (user) => user === tabs.permission
                            );
                            if (temp.length > 0)
                              userTabs.push({
                                to: tabs.to,
                              });
                          });
                          userTabs.shift();
                          const index = userTabs.findIndex((c) => c.to === url);
                          setSelectedIndex(index);
                        }}
                      >
                        {rowViewData?.lockSmithVendor}
                      </div>
                    </span>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Address:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {rowViewData?.street} {rowViewData?.city} {", "}{" "}
                        {rowViewData?.state} {rowViewData?.zipCode}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Vendor Phone:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {rowViewData?.okvendorPhone !== "null"
                          ? rowViewData?.okvendorPhone
                          : ""}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      # of Keys:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">{rowViewData?.numberOfKeys}</span>
                    </div>
                  </div>
                </div>
                <div className="p-2 divide-y">
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Key Assigned To:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {rowViewData?.keysAssignedTo
                          ? keysAssignedValue?.map((x) => x?.label)?.join(", ")
                          : null}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Key Type:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">{rowViewData?.keyType}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 p-2">
                    <label className="col-span-1 mx-4 expandedLabel fontExpand">
                      Key Coding Model:
                    </label>
                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {rowViewData?.keyCodingModel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  );
};
